import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Command } from "./Command";
import { findLayer } from "../document/LayerUtils";
import { setPos } from "./MoveLayerCommand";
import { getLayerAlignRect, type LayerRect } from "./AlignLayersCommand";

// DistributeLayersCommand — Layer ▸ Distribute ▸ {Top/V-Center/Bottom/
// Left/H-Center/Right} Edges/Centers (audit i3).
//
// Photoshop semantics: sort the participating layers along the chosen axis
// by the anchor point the op cares about (an edge or a center), then space
// every layer so consecutive anchors are equidistant — the two OUTERMOST
// layers (by that anchor) stay exactly where they are; only the layers
// between them move. Needs 3+ layers: with fewer there's nothing "between"
// the extremes to redistribute.
//
// Shaped like AlignLayersCommand/MoveLayersCommand (per-layer absolute
// from/to, one atomic step, invert() swaps from/to).

/** The 6 Photoshop distribute operations. Top/V-Center/Bottom distribute
 * along Y; Left/H-Center/Right distribute along X. */
export type DistributeOp = "top" | "v-center" | "bottom" | "left" | "h-center" | "right";

/** A single layer's absolute from/to move, one entry per affected layer. */
export interface LayerDistributeEntry {
  readonly layerId: string;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/** Same guard AlignLayersCommand uses — hidden/locked layers don't move. */
function isDistributable(layer: Layer): boolean {
  return layer.visible && !layer.lockAll && !layer.lockPosition;
}

function anchorOf(op: DistributeOp, r: LayerRect): number {
  switch (op) {
    case "top": return r.y;
    case "bottom": return r.y + r.h;
    case "v-center": return r.y + r.h / 2;
    case "left": return r.x;
    case "right": return r.x + r.w;
    case "h-center": return r.x + r.w / 2;
  }
}

/**
 * Build the DistributeLayersCommand for `op` over `layerIds`, or null when
 * fewer than 3 layers resolve to alignable targets.
 */
export function planDistributeLayers(
  doc: Document,
  layerIds: readonly string[],
  op: DistributeOp,
): DistributeLayersCommand | null {
  const seen = new Set<string>();
  const targets: Layer[] = [];
  for (const id of layerIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const l = findLayer(doc.layers, id);
    if (l && isDistributable(l)) targets.push(l);
  }
  if (targets.length < 3) return null;

  const isVertical = op === "top" || op === "v-center" || op === "bottom";

  const items = targets.map((layer) => ({ layer, rect: getLayerAlignRect(layer, doc) }));
  items.sort((a, b) => anchorOf(op, a.rect) - anchorOf(op, b.rect));

  const firstAnchor = anchorOf(op, items[0]!.rect);
  const lastAnchor = anchorOf(op, items[items.length - 1]!.rect);
  const step = (lastAnchor - firstAnchor) / (items.length - 1);

  const entries: LayerDistributeEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const { layer, rect } = items[i]!;
    const targetAnchor = firstAnchor + step * i;
    const delta = Math.round(targetAnchor - anchorOf(op, rect));
    const dx = isVertical ? 0 : delta;
    const dy = isVertical ? delta : 0;
    entries.push({
      layerId: layer.id,
      fromX: layer.x,
      fromY: layer.y,
      toX: layer.x + dx,
      toY: layer.y + dy,
    });
  }
  return new DistributeLayersCommand(entries);
}

export class DistributeLayersCommand implements Command {
  readonly kind = "distribute-layers";

  constructor(private readonly moves: readonly LayerDistributeEntry[]) {}

  apply(doc: Document): Document {
    let layers = doc.layers;
    for (const m of this.moves) {
      layers = setPos(layers, m.layerId, m.toX, m.toY);
    }
    return { ...doc, layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new DistributeLayersCommand(
      this.moves.map((m) => ({
        layerId: m.layerId,
        fromX: m.toX,
        fromY: m.toY,
        toX: m.fromX,
        toY: m.fromY,
      })),
    );
  }
}
